import React, { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { damga, yazdirBaslikli } from "../lib/stamp";
import { toast } from "sonner";
import { Cog, Download, Loader2, X, Filter, Gauge, Package, Scale, Printer, Clock, Award, ShieldCheck } from "lucide-react";

const nf = (n) => Number(n || 0).toLocaleString("tr-TR");
const esc = (v) => String(v ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const verimCls = (p) =>
  p >= 100 ? "bg-emerald-100 text-emerald-700" : p >= 70 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700";

function Verim({ p, testid }) {
  return <span data-testid={testid} className={`inline-block rounded-md px-2.5 py-1 text-xs font-bold num ${verimCls(p)}`}>%{p}</span>;
}

const VLABELS = ["00:00 - 08:00", "08:00 - 16:00", "16:00 - 24:00"];
const vShort = (l) => `${l.split(" - ")[0].slice(0, 2)}-${l.split(" - ")[1].slice(0, 2)}`;
const vNet = (r, label) => {
  const v = (r.vardiyalar || []).find((x) => x.vardiya === label);
  return v ? v.net : 0;
};

function exportExcel(rows) {
  const vh = VLABELS.map((l) => `<th>${vShort(l)} Net</th>`).join("");
  let body = `<tr style="background:#18181B;color:#fff;font-weight:bold;"><th>Makina</th><th>Ürün Çeşidi</th><th>Şekillenen</th><th>Defo</th><th>Net</th><th>KG</th><th>Kapasite Hedef</th><th>Gün</th><th>Verimlilik %</th><th>Defo %</th>${vh}</tr>`;
  rows.forEach((r) => {
    const vc = VLABELS.map((l) => `<td>${vNet(r, l)}</td>`).join("");
    body += `<tr><td>${esc(r.makina)}</td><td>${r.urun_cesidi}</td><td>${r.sekillenen}</td><td>${r.defo}</td><td>${r.net}</td><td>${r.kg}</td><td>${r.kapasite > 0 ? r.hedef : ""}</td><td>${r.gun_sayisi}</td><td>${r.kapasite > 0 ? r.yuzde : ""}</td><td>${r.defo_orani}</td>${vc}</tr>`;
  });
  const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="utf-8"></head><body><table border="1">${body}</table></body></html>`;
  const blob = new Blob(["\ufeff", html], { type: "application/vnd.ms-excel" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = `Makine Performansı - ${damga()}.xls`; a.click();
  URL.revokeObjectURL(url);
}

function DetayModal({ row, start, end, onClose }) {
  const makina = row.makina;
  const [rows, setRows] = useState(null);
  useEffect(() => {
    const params = { makina };
    if (start) params.start = start;
    if (end) params.end = end;
    api.get("/dashboard/makine-performans-detay", { params })
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]));
  }, [makina, start, end]);

  const tot = (rows || []).reduce((a, r) => ({
    sekillenen: a.sekillenen + r.sekillenen, defo: a.defo + r.defo, net: a.net + r.net, net_kg: a.net_kg + r.net_kg,
  }), { sekillenen: 0, defo: 0, net: 0, net_kg: 0 });

  const vardiyalar = row.vardiyalar || [];

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="makine-detay-modal">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-5xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 bg-zinc-800 text-white rounded-t-lg">
          <span className="flex items-center gap-2 font-head font-bold text-sm uppercase tracking-wide"><Cog size={16} /> {makina} — Ürün & Vardiya Kırılımı</span>
          <button onClick={onClose} data-testid="makine-detay-close" className="text-zinc-300 hover:text-white"><X size={18} /></button>
        </div>
        <div className="overflow-auto p-4 space-y-4">
          {/* Vardiya bazında özet */}
          {vardiyalar.length > 0 && (
            <div data-testid="vardiya-ozet">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-2"><Clock size={13} /> Vardiya Bazında</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {VLABELS.map((l) => {
                  const v = vardiyalar.find((x) => x.vardiya === l);
                  return (
                    <div key={l} className="border border-zinc-200 rounded-lg p-3 bg-zinc-50" data-testid={`vardiya-card-${vShort(l)}`}>
                      <div className="text-xs font-bold text-zinc-700 mb-1.5 flex items-center gap-1.5"><Clock size={12} className="text-amber-600" /> {l}</div>
                      {v ? (
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between"><span className="text-zinc-500">Net</span><span className="font-bold num">{nf(v.net)}</span></div>
                          <div className="flex justify-between"><span className="text-zinc-500">KG</span><span className="num">{nf(v.kg)}</span></div>
                          <div className="flex justify-between"><span className="text-zinc-500">Defo</span><span className="num text-red-600">{nf(v.defo)} (%{v.defo_orani})</span></div>
                        </div>
                      ) : <div className="text-xs text-zinc-400 py-2">Kayıt yok</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {/* Ürün kırılımı */}
          {rows === null ? (
            <div className="text-zinc-400 py-10 text-center"><Loader2 className="animate-spin inline" size={18} /> Yükleniyor...</div>
          ) : rows.length === 0 ? (
            <div className="text-zinc-400 py-10 text-center">Bu makine için üretim kaydı yok.</div>
          ) : (
            <table className="w-full data-table text-sm">
              <thead>
                <tr>
                  <th className="w-36">Kalıp Kodu</th><th>Ürün Açıklaması</th>
                  <th className="text-right">Şekillenen</th><th className="text-right">Defo</th>
                  <th className="text-right">Net</th><th className="text-right">Net KG</th>
                  <th className="text-center">Defo %</th><th className="text-center">Kapasite Verimliliği</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.kalip_kodu} className={i % 2 ? "bg-zinc-50/60" : "bg-white"} data-testid={`detay-row-${r.kalip_kodu}`}>
                    <td className="mono font-semibold text-zinc-900">{r.kalip_kodu}</td>
                    <td className="text-zinc-700">{r.aciklama || "—"}</td>
                    <td className="text-right num">{nf(r.sekillenen)}</td>
                    <td className="text-right num text-red-600">{nf(r.defo)}</td>
                    <td className="text-right num font-semibold">{nf(r.net)}</td>
                    <td className="text-right num">{nf(r.net_kg)}</td>
                    <td className="text-center"><span className={`inline-block rounded px-2 py-0.5 text-xs font-bold ${r.defo_orani >= 10 ? "bg-red-100 text-red-700" : r.defo_orani >= 5 ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>%{r.defo_orani}</span></td>
                    <td className="text-center">{r.kapasite > 0 ? <Verim p={r.yuzde} testid={`detay-verim-${r.kalip_kodu}`} /> : <span className="text-zinc-400 text-xs">kapasite yok</span>}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-zinc-100 font-bold border-t-2 border-zinc-300">
                  <td colSpan={2} className="text-zinc-900">TOPLAM</td>
                  <td className="text-right num">{nf(tot.sekillenen)}</td>
                  <td className="text-right num text-red-600">{nf(tot.defo)}</td>
                  <td className="text-right num">{nf(tot.net)}</td>
                  <td className="text-right num">{nf(Math.round(tot.net_kg))}</td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export default function MakinePerformans() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [sel, setSel] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (start) params.start = start;
    if (end) params.end = end;
    api.get("/dashboard/makine-performans", { params })
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [start, end]);

  useEffect(() => { load(); }, [load]);

  const tot = rows.reduce((a, r) => ({
    net: a.net + r.net, kg: a.kg + r.kg, sekillenen: a.sekillenen + r.sekillenen, defo: a.defo + r.defo,
  }), { net: 0, kg: 0, sekillenen: 0, defo: 0 });

  const bestVerim = rows.filter((r) => r.kapasite > 0).reduce((b, r) => (!b || r.yuzde > b.yuzde ? r : b), null);
  const leastDefo = rows.filter((r) => r.brut > 0).reduce((b, r) => (!b || r.defo_orani < b.defo_orani ? r : b), null);

  return (
    <>
      <PageHeader title="Makine Performansı" subtitle="Fiziksel makine bazında üretim (Şekillenen Ürün) — makineye tıklayın, ürün ve vardiya kırılımını görün"
        actions={
          <div className="flex items-center gap-2 no-print">
            <button onClick={() => exportExcel(rows)} data-testid="export-btn" disabled={!rows.length}
              className="flex items-center gap-2 border border-zinc-300 hover:bg-zinc-50 text-zinc-700 text-sm font-semibold px-3 py-2 rounded-sm disabled:opacity-40">
              <Download size={15} /> Excel'e Aktar
            </button>
            <button onClick={() => yazdirBaslikli("Makine Performansı")} data-testid="print-btn" disabled={!rows.length}
              className="flex items-center gap-2 border border-zinc-300 hover:bg-zinc-50 text-zinc-700 text-sm font-semibold px-3 py-2 rounded-sm disabled:opacity-40">
              <Printer size={15} /> PDF / Yazdır
            </button>
          </div>
        } />
      <div className="p-6 space-y-5 print-area" data-testid="makine-performans-page">
        <div className="flex items-end gap-3 flex-wrap bg-zinc-50 border border-zinc-200 rounded-sm px-3 py-2.5 no-print" data-testid="filter-bar">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-zinc-500 uppercase tracking-wide pb-2"><Filter size={13} /> Tarih Aralığı</div>
          <div><label className="block text-[10px] font-semibold text-zinc-500 uppercase mb-0.5">Başlangıç</label>
            <input type="date" data-testid="filter-start" value={start} onChange={(e) => setStart(e.target.value)}
              className="border border-zinc-300 rounded-sm px-2 py-1.5 text-sm mono focus:outline-none focus:ring-2 focus:ring-amber-600" /></div>
          <div><label className="block text-[10px] font-semibold text-zinc-500 uppercase mb-0.5">Bitiş</label>
            <input type="date" data-testid="filter-end" value={end} onChange={(e) => setEnd(e.target.value)}
              className="border border-zinc-300 rounded-sm px-2 py-1.5 text-sm mono focus:outline-none focus:ring-2 focus:ring-amber-600" /></div>
          <button onClick={() => { setStart(""); setEnd(""); }} data-testid="filter-clear" className="text-xs text-zinc-500 hover:text-red-600 pb-2 underline">Temizle</button>
        </div>

        {loading ? (
          <div className="text-zinc-400 py-10 text-center"><Loader2 className="animate-spin inline" size={18} /> Yükleniyor...</div>
        ) : rows.length === 0 ? (
          <div className="text-zinc-400 py-10 text-center border border-zinc-200 rounded-sm">Seçili aralıkta üretim kaydı yok.</div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="makine-kartlari">
              {rows.map((r) => (
                <button key={r.makina} onClick={() => setSel(r)} data-testid={`makine-card-${r.makina}`}
                  className="text-left border border-zinc-200 rounded-lg overflow-hidden hover:ring-2 hover:ring-amber-500 transition-all bg-white shadow-sm hover:shadow-md">
                  <div className="flex items-center justify-between px-4 py-2.5 bg-zinc-800 text-white">
                    <span className="flex items-center gap-2 font-head font-bold text-sm"><Cog size={15} /> {r.makina}</span>
                    {r.kapasite > 0 ? <Verim p={r.yuzde} testid={`card-verim-${r.makina}`} /> : <span className="text-[10px] text-zinc-400 uppercase">kapasite yok</span>}
                  </div>
                  <div className="p-4 space-y-2.5">
                    {(r.makina === bestVerim?.makina || r.makina === leastDefo?.makina) && (
                      <div className="flex flex-wrap gap-1.5" data-testid={`makine-rozet-${r.makina}`}>
                        {r.makina === bestVerim?.makina && (
                          <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 text-[11px] font-bold px-2 py-0.5 rounded-full" data-testid="rozet-en-verimli">
                            <Award size={12} /> En Verimli %{bestVerim.yuzde}
                          </span>
                        )}
                        {r.makina === leastDefo?.makina && (
                          <span className="inline-flex items-center gap-1 bg-sky-100 text-sky-700 text-[11px] font-bold px-2 py-0.5 rounded-full" data-testid="rozet-en-az-defo">
                            <ShieldCheck size={12} /> En Az Defo %{leastDefo.defo_orani}
                          </span>
                        )}
                      </div>
                    )}
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-1.5 text-zinc-500"><Package size={14} /> Net Üretim</span>
                      <span className="font-bold text-zinc-900 num">{nf(r.net)}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-1.5 text-zinc-500"><Scale size={14} /> Toplam KG</span>
                      <span className="font-semibold text-zinc-700 num">{nf(r.kg)}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-1.5 text-zinc-500"><Gauge size={14} /> Kapasite Hedef</span>
                      <span className="text-zinc-600 num">{r.kapasite > 0 ? `${nf(r.hedef)} (${r.gun_sayisi} gün)` : "—"}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs pt-1 border-t border-zinc-100">
                      <span className="text-zinc-400">{r.urun_cesidi} ürün çeşidi · Defo %{r.defo_orani}</span>
                      {!r.planli && <span className="text-amber-600 font-semibold" title="Şekillendirme Planı'nda eşleşmeyen (süreç tipi) makine">süreç tipi</span>}
                    </div>
                  </div>
                </button>
              ))}
            </div>

            <div className="border border-zinc-200 rounded-sm overflow-x-auto" data-testid="makine-tablo">
              <table className="w-full data-table text-sm">
                <thead>
                  <tr>
                    <th>Makina</th><th className="text-right">Ürün Çeşidi</th><th className="text-right">Şekillenen</th>
                    <th className="text-right">Defo</th><th className="text-right">Net</th><th className="text-right">KG</th>
                    <th className="text-right">Kapasite Hedef</th><th className="text-center">Gün</th><th className="text-center">Verimlilik</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={r.makina} onClick={() => setSel(r)} className={`cursor-pointer hover:bg-amber-50 ${i % 2 ? "bg-zinc-50/60" : "bg-white"}`} data-testid={`makine-tr-${r.makina}`}>
                      <td className="font-semibold text-zinc-900 flex items-center gap-2"><Cog size={14} className="text-zinc-400" /> {r.makina}
                        {r.makina === bestVerim?.makina && <span title="En Verimli" className="inline-flex items-center text-emerald-600"><Award size={14} /></span>}
                        {r.makina === leastDefo?.makina && <span title="En Az Defo" className="inline-flex items-center text-sky-600"><ShieldCheck size={14} /></span>}
                      </td>
                      <td className="text-right num">{r.urun_cesidi}</td>
                      <td className="text-right num">{nf(r.sekillenen)}</td>
                      <td className="text-right num text-red-600">{nf(r.defo)}</td>
                      <td className="text-right num font-semibold">{nf(r.net)}</td>
                      <td className="text-right num">{nf(r.kg)}</td>
                      <td className="text-right num">{r.kapasite > 0 ? nf(r.hedef) : "—"}</td>
                      <td className="text-center num">{r.gun_sayisi}</td>
                      <td className="text-center">{r.kapasite > 0 ? <Verim p={r.yuzde} testid={`tr-verim-${r.makina}`} /> : <span className="text-zinc-400 text-xs">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-zinc-100 font-bold border-t-2 border-zinc-300">
                    <td className="text-zinc-900">TOPLAM ({rows.length} makine)</td>
                    <td></td>
                    <td className="text-right num">{nf(tot.sekillenen)}</td>
                    <td className="text-right num text-red-600">{nf(tot.defo)}</td>
                    <td className="text-right num">{nf(tot.net)}</td>
                    <td className="text-right num">{nf(Math.round(tot.kg))}</td>
                    <td colSpan={3}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        )}
      </div>
      {sel && <DetayModal row={sel} start={start} end={end} onClose={() => setSel(null)} />}
    </>
  );
}
